import React, { useEffect, useState } from "react";
import api, { formatError } from "@/lib/api";
import { payWithRazorpay } from "@/lib/razorpay";
import { toast } from "sonner";
import LocationAutocomplete from "@/components/LocationAutocomplete";
import Navbar from "@/components/Navbar";
import BackButton from "@/components/BackButton";
import { Plane, Car, Bus, ArrowRight, CheckCircle2 } from "lucide-react";
import ClosedFeature from "@/components/ClosedFeature";
import { useAuth } from "@/contexts/AuthContext";



const SERVICES = [
  { id: "airport_pickup", label: "Airport Pickup", icon: Plane, price: 35, unit: "/ trip", vehicle: "Sedan / SUV (up to 4 pax)", desc: "Bengaluru Kempegowda Intl. (BLR) → your hotel", features: ["24/7 Meet & Greet", "Luggage included", "Flight tracking", "Bottled water onboard"], preset: { passengers: 1, drop: "" } },
  { id: "airport_drop", label: "Airport Drop", icon: Plane, price: 35, unit: "/ trip", vehicle: "Sedan / SUV (up to 4 pax)", desc: "Your hotel → BLR airport", features: ["3 hr buffer recommended", "Real-time traffic routing", "Luggage included", "24/7 availability"], preset: { passengers: 1, pickup: "" } },
  { id: "local_cab", label: "Local Cab", icon: Car, price: 25, unit: "/ 4hr block", vehicle: "A/C Sedan (up to 4 pax)", desc: "City-wide rides, point-to-point", features: ["4 hr block included", "40 km included", "English-speaking driver", "Hourly extension available"], preset: { passengers: 1 } },
  { id: "team_bus", label: "Team Bus", icon: Bus, price: 120, unit: "/ half-day", vehicle: "25-seater A/C Coach", desc: "Groups of 8+, half-day inclusive", features: ["Venue + city tour", "Half-day (5 hrs)", "On-board guide", "Ideal for federations & relay teams"], preset: { passengers: 8 } }
];

const STATUS_COLOR = {
  approved: "text-[#00E676]",
  completed: "text-[#94A3B8]",
  pending: "text-[#00F0FF]"
};

export default function Logistics() {
  const { user } = useAuth();
  const [type, setType] = useState("airport_pickup");
  const [requests, setRequests] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    pickup_location: "Kempegowda International Airport (BLR)",
    drop_location: "",
    date_time: "",
    flight_number: "",
    passengers: 1,
    notes: ""
  });
  const AIRPORT = "Bengaluru Kempegowda International Airport (BLR)";

  const selectedService = SERVICES.find((s) => s.id === type) || SERVICES[0];

  const selectService = (newType) => {
    setType(newType);
    if (newType === "airport_pickup") {
      setForm((p) => ({ ...p, pickup_location: AIRPORT }));
    } else if (newType === "airport_drop") {
      setForm((p) => ({ ...p, drop_location: AIRPORT }));
    }
  };

  const pickupReadOnly = type === "airport_pickup";
  const dropReadOnly = type === "airport_drop";

  const [meta, setMeta] = useState(null);

  useEffect(() => { 
    api.get("/meta/events").then((r) => setMeta(r.data)).catch(() => { });
    api.get("/logistics/me").then((r) => setRequests(r.data)).catch(() => { }); 
  }, []);

  if (meta && !meta.registration_open && user?.role !== "admin") {
    return (
      <ClosedFeature
        featureName="Travel & Logistics"
        icon={Plane}
        subtitle="OPENING SOON"
        description="Travel and logistics booking will be opening soon."
      />
    );
  }

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/logistics", { ...form, service_type: type });
      toast.success(`Request submitted: ${data.request_id}`);
      setRequests((p) => [data, ...p]);
      setForm({ pickup_location: "", drop_location: "", date_time: "", flight_number: "", passengers: 1, notes: "" });
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setSubmitting(false); }
  };

  const payRequest = async (id) => {
    try {
      const result = await payWithRazorpay({ logistics_id: id }, user);
      if (!result) return; // user dismissed the payment modal
      toast.success("Payment successful");
      const r = await api.get("/logistics/me");
      setRequests(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail ? formatError(e.response.data.detail) : e.message);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B1429]">
      <Navbar />
      <div className="pt-28 pb-20 max-w-6xl mx-auto px-6 lg:px-10">
        <BackButton className="mb-6" />
        <div className="eyebrow mb-3">Travel & Logistics</div>
        <h1 className="font-display font-black text-4xl md:text-5xl mb-2">Get to the <span className="text-[#00F0FF]">starting block</span></h1>
        <p className="text-[#94A3B8] mb-10">Airport transfers, local cabs and team buses managed by AAM 2026 logistics.</p>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {SERVICES.map((s) => (
            <button key={s.id} onClick={() => selectService(s.id)} data-testid={`service-${s.id}`}
              className={`card-dark p-5 text-left transition-all ${type === s.id ? "border-[#00F0FF] ring-1 ring-[#00F0FF]/40" : "hover:border-white/30"}`}>
              <div className="flex items-start justify-between">
                <s.icon className="w-6 h-6 text-[#00F0FF]" strokeWidth={1.5} />
                {type === s.id && <CheckCircle2 className="w-5 h-5 text-[#00F0FF]" />}
              </div>
              <div className="font-display font-bold mt-3 text-white">{s.label}</div>
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8] mt-1">{s.vehicle}</div>
              <div className="text-xs text-[#94A3B8] mt-2">{s.desc}</div>
              <ul className="mt-3 space-y-1">
                {s.features.map((f) => (
                  <li key={f} className="text-[11px] text-[#CBD5E1] flex items-start gap-1.5">
                    <CheckCircle2 className="w-3 h-3 text-[#00F0FF] shrink-0 mt-0.5" />{f}
                  </li>
                ))}
              </ul>
              <div className="text-[#00F0FF] font-bold mt-3 text-lg">${s.price} <span className="text-xs text-[#94A3B8] font-normal">USD {s.unit}</span></div>
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="card-dark p-6 grid md:grid-cols-2 gap-4 mb-10" data-testid="logistics-form">
          <div className="md:col-span-2 flex items-center justify-between rounded-lg bg-[#00F0FF]/5 border border-[#00F0FF]/20 px-4 py-3">
            <div className="flex items-center gap-3">
              <selectedService.icon className="w-5 h-5 text-[#00F0FF]" strokeWidth={1.5} />
              <div>
                <div className="text-sm font-semibold text-white" data-testid="selected-service-label">{selectedService.label}</div>
                <div className="text-[10px] uppercase tracking-[0.18em] text-[#94A3B8]">{selectedService.vehicle}</div>
              </div>
            </div>
            <div className="text-right">
              <div className="text-[#00F0FF] font-bold text-lg" data-testid="selected-service-price">${selectedService.price}</div>
              <div className="text-[10px] text-[#94A3B8]">USD</div>
            </div>
          </div>
          <div>
            <label className="text-xs uppercase text-[#94A3B8] mb-1.5 block">Pickup Location {pickupReadOnly && <span className="text-[#00F0FF]">(fixed)</span>}</label>
            <LocationAutocomplete
              value={form.pickup_location}
              onChange={(val) => setForm({ ...form, pickup_location: val })}
              onSelect={(val) => setForm({ ...form, pickup_location: val })}
              readOnly={pickupReadOnly}
              required
              testId="pickup"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-[#94A3B8] mb-1.5 block">Drop Location {dropReadOnly && <span className="text-[#00F0FF]">(fixed)</span>}</label>
            <LocationAutocomplete
              value={form.drop_location}
              onChange={(val) => setForm({ ...form, drop_location: val })}
              onSelect={(val) => setForm({ ...form, drop_location: val })}
              readOnly={dropReadOnly}
              required
              testId="drop"
            />
          </div>
          <div>
            <label className="text-xs uppercase text-[#94A3B8] mb-1.5 block">Date & Time</label>
            <input type="datetime-local" value={form.date_time} onChange={(e) => setForm({ ...form, date_time: e.target.value })} className="input-dark" required data-testid="datetime" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#94A3B8] mb-1.5 block">Flight Number (if airport)</label>
            <input value={form.flight_number} onChange={(e) => setForm({ ...form, flight_number: e.target.value })} className="input-dark" placeholder="e.g. AI 805" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#94A3B8] mb-1.5 block">Passengers</label>
            <input type="number" min={1} value={form.passengers} onChange={(e) => setForm({ ...form, passengers: parseInt(e.target.value) || 1 })} className="input-dark" />
          </div>
          <div>
            <label className="text-xs uppercase text-[#94A3B8] mb-1.5 block">Notes</label>
            <input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="input-dark" />
          </div>
          <div className="md:col-span-2">
            <button disabled={submitting} className="btn-primary w-full justify-center" data-testid="submit-logistics">
              {submitting ? "Submitting..." : "Submit Request"} <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </form>

        <div className="eyebrow mb-3">Your Requests</div>
        {requests.length === 0 && <div className="text-sm text-[#94A3B8]">No requests yet.</div>}
        <div className="space-y-3">
          {requests.map((r) => (
            <div key={r.request_id} className="card-dark p-5 flex flex-wrap justify-between items-center gap-3" data-testid={`req-${r.request_id}`}>
              <div>
                <div className="font-semibold">{r.service_type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}</div>
                <div className="text-xs text-[#94A3B8] mt-0.5">{r.pickup_location} → {r.drop_location}</div>
                <div className="text-xs text-[#94A3B8]">{r.date_time} · ${r.amount_usd} · <span className={STATUS_COLOR[r.status] || "text-[#94A3B8]"}>{r.status}</span></div>
              </div>
              <div className="flex flex-col gap-2 items-end">
                {r.payment_status === "paid"
                  ? <>
                    <span className="text-[#00E676] text-xs font-semibold flex items-center gap-1"><CheckCircle2 className="w-4 h-4" />PAID</span>
                    <a href={`/invoice/logistics/${r.request_id}`} className="text-[#00F0FF] hover:underline text-xs">Download Invoice →</a>
                  </>
                  : <button onClick={() => payRequest(r.request_id)} className="btn-primary !py-2 !px-4 text-xs">Pay ${r.amount_usd}</button>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
